import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  Button,
  IconButton,
  Box,
  FormHelperText,
  Divider,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";
import { CleaveNumberInput } from "src/@core/components/cleave-components";

const schema = yup.object().shape({
  name: yup.string().required("Category name is required"),
  type: yup.number().required("Type is required"),
});

const DialogAddCategory = ({ open, onClose, onSuccess, mode, dataToEdit }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: dataToEdit?.name || "",
      type: dataToEdit?.type || 0,
    },
    resolver: yupResolver(schema),
  });

  // Reset form when dataToEdit changes
  React.useEffect(() => {
    if (dataToEdit && mode === "edit") {
      reset({
        name: dataToEdit.name || "",
        type: dataToEdit.type || 0,
      });
    } else {
      reset({
        name: "",
        type: 0,
      });
    }
  }, [dataToEdit, mode, reset]);

  const [loading, setLoading] = useState(false);
  
  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#fff",
      height: "48px",
      "& fieldset": {
        border: "1px solid #B3C4CC",
      },
      "&:hover fieldset": {
        borderColor: "#D1D5DB",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#17D96C",
        borderWidth: "2px",
      },
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "14px",
      color: "#000",
      "&::placeholder": {
        color: "#A3B1C0",
        fontWeight: 500,
        opacity: 1,
      },
    },
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data) => {
    setLoading(true);
    
    let payload = {};
    
    if (mode === "edit") {
      payload = {
        name: data.name,
        type: 0,
        is_active: dataToEdit?.is_active || true,
      };
    } else {
      payload = {
        name: data.name,
        type: 0,
      };
    }

    let apiInstance = null;
    if (mode === "edit") {
      apiInstance = axiosInstance.patch(
        ApiEndPoints.CATEGORIES.update(dataToEdit.id),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } else {
      apiInstance = axiosInstance.post(ApiEndPoints.CATEGORIES.create, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    }

    apiInstance
      .then((response) => response.data)
      .then((response) => {
        onSuccess();
        toastSuccess(response.message);
        onClose();
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="fm-h6"
            sx={{ fontWeight: 800, color: "#000000" }}
          >
            {mode === "edit" ? "Edit Category" : "Add New Category"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <HighlightOffIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Category Name */}
          <FormControl fullWidth sx={{ mb: 5 }}>
            <FormLabel
              htmlFor="name"
              sx={{
                fontSize: "12px",
                color: "#6C7278",
                fontWeight: 500,
                mb: 1,
              }}
            >
              Category Name
            </FormLabel>
            <Controller
              name="name"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  onChange={onChange}
                  id="name"
                  value={value}
                  placeholder="Enter Category Name"
                  sx={textFieldStyles}
                  variant="outlined"
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              )}
            />
            {errors.name && (
              <FormHelperText sx={{ color: "error.main" }}>
                {errors.name.message}
              </FormHelperText>
            )}
          </FormControl>

          {/* Type */}
          {/* <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel
              htmlFor="type"
              sx={{
                fontSize: "12px",
                color: "#6C7278",
                fontWeight: 500,
                mb: 1,
              }}
            >
              Type
            </FormLabel>
            <Controller
              name="type"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  onChange={(e) => onChange(parseInt(e.target.value))}
                  id="type"
                  value={value}
                  placeholder="Enter Type (0 for default)"
                  sx={textFieldStyles}
                  variant="outlined"
                  InputProps={{
                    disableUnderline: true,
                    inputComponent: CleaveNumberInput,
                  }}
                />
              )}
            />
            {errors.type && (
              <FormHelperText sx={{ color: "error.main" }}>
                {errors.type.message}
              </FormHelperText>
            )}
          </FormControl> */}
        </form>
      </DialogContent>
      
      <DialogActions sx={{ gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: "#000000",
            borderColor: "#CFCFCF",
            fontSize: "16px",
            fontWeight: 600,
            padding: "12px 24px",
            borderRadius: "8px",
            width: "100%",
            height: "48px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: "#17D96C",
            color: "#FFFFFF",
            fontSize: "16px",
            fontWeight: 600,
            padding: "20px 24px",
            width: "100%",
            borderRadius: "12px",
            height: "48px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#17D96C",
            },
            "&:disabled": {
              backgroundColor: "#B3C4CC",
            },
          }}
        >
          {loading ? "Processing..." : mode === "edit" ? "Update Category" : "Add Category"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogAddCategory;
